import { useCallback, useEffect, useState } from "react";
import { Download, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errorMessage } from "../../lib/api";
import { DetailSheet, STATUS_LABEL } from "./DetailSheet";

const COLS = {
  inquiries: [["company", "Unternehmen"], ["position", "Gesuchter Beruf"], ["contact_name", "Ansprechpartner"]],
  applicants: [["name", "Name"], ["profession", "Beruf"], ["country", "Wohnsitzland"]],
};

export const InboxTable = ({ kind, odooReady }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/admin/${kind}`, { params: { q, status } });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  }, [kind, q, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await http.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `pactora-${kind}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Export fehlgeschlagen."); }
  };

  const onChanged = (updated, removedId) => {
    if (removedId) { setItems((l) => l.filter((x) => x.id !== removedId)); return; }
    setItems((l) => l.map((x) => (x.id === updated.id ? updated : x)));
    setSelected(updated);
  };

  const cell = (it, k) => (k === "name" ? `${it.first_name ?? ""} ${it.last_name ?? ""}` : it[k] || "–");

  return (
    <div data-testid={`inbox-${kind}`}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input aria-label="Suche" placeholder="Suchen nach Name, E-Mail, Beruf, Referenz …" value={q} onChange={(e) => setQ(e.target.value)} className="field-input" style={{ paddingLeft: "2.6rem" }} data-testid={`inbox-search-${kind}`} />
        </div>
        <select aria-label="Status filtern" value={status} onChange={(e) => setStatus(e.target.value)} className="field-input md:w-56" data-testid={`inbox-status-filter-${kind}`}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button onClick={exportCsv} className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--navy)] px-5 py-3 text-sm font-semibold text-white" data-testid={`inbox-export-${kind}`}>
          <Download size={16} /> CSV exportieren
        </button>
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Referenz</th>{COLS[kind].map(([, l]) => <th key={l} className="px-4 py-3">{l}</th>)}<th className="px-4 py-3">Eingang</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid={`inbox-empty-${kind}`}>Noch keine Einträge.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} onClick={() => setSelected(it)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" data-testid={`inbox-row-${it.reference}`}>
                <td className="px-4 py-3 font-mono text-xs">
                  <button className="underline-offset-2 hover:underline" onClick={(e) => { e.stopPropagation(); setSelected(it); }}>{it.reference}</button>
                </td>
                {COLS[kind].map(([k]) => <td key={k} className="px-4 py-3">{cell(it, k)}</td>)}
                <td className="px-4 py-3 text-slate-500">{it.created_at_display}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${it.status === "neu" ? "bg-[color:var(--teal)] text-white" : "bg-slate-100 text-slate-700"}`}>{STATUS_LABEL[it.status] ?? it.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && <DetailSheet key={selected.id} kind={kind} item={selected} onClose={() => setSelected(null)} onChanged={onChanged} odooReady={odooReady} />}
    </div>
  );
};
